import React from 'react';

// Project Nova Design System styles
const styles = {
  primaryColor: "#1d0c46", // Deep Indigo
  secondaryColor: "#caa968", // Muted Gold
  backgroundColor: "#f8fafc", // Light gray background
  textColor: "#0f172a",
  textHeaders: "#1d0c46", // Deep Indigo for headers
  borderColor: "rgba(255, 255, 255, 0.18)", // Glass border
  glassEffect: "rgba(255, 255, 255, 0.7)",
  glassBorder: "rgba(255, 255, 255, 0.18)",
  cardShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
  cardShadowHover: "0 4px 16px rgba(0, 0, 0, 0.1)"
};

// Base skeleton element with Project Nova styling
const SkeletonElement = ({ className = "", width = "100%", height = "20px", style = {} }) => (
  <div
    className={`animate-pulse rounded bg-gray-200 ${className}`}
    style={{
      width,
      height,
      ...style
    }}
  />
);

// Analytics Card Skeleton
export const AnalyticsCardSkeleton = () => (
  <div 
    className="glass-effect rounded-[12px] p-4 animate-pulse"
    style={{
      backgroundColor: styles.glassEffect,
      border: `1px solid ${styles.borderColor}`,
      boxShadow: styles.cardShadow
    }}
  >
    <SkeletonElement height="16px" width="60%" className="mb-3" />
    <SkeletonElement height="32px" width="80%" className="mb-2" />
    <SkeletonElement height="14px" width="40%" />
  </div>
);

// Analytics Cards Grid Skeleton
export const AnalyticsCardsSkeleton = () => (
  <div className="w-full grid grid-rows-2 gap-6 h-auto mb-6">
    {/* First row - 4 cards */}
    <div className="grid grid-cols-4 gap-6">
      {[1, 2, 3, 4].map((i) => (
        <AnalyticsCardSkeleton key={i} />
      ))}
    </div>
    
    {/* Second row - 3 cards */}
    <div className="grid grid-cols-3 gap-6">
      {[1, 2, 3].map((i) => (
        <AnalyticsCardSkeleton key={i} />
      ))}
    </div>
  </div>
);

// Chart Skeleton
export const ChartSkeleton = ({ height = "400px" }) => (
  <div 
    className="glass-effect rounded-[12px] p-6 animate-pulse"
    style={{
      backgroundColor: styles.glassEffect,
      border: `1px solid ${styles.borderColor}`,
      boxShadow: styles.cardShadow
    }}
  >
    <div className="flex items-center mb-4">
      <div className="w-2 h-8 mr-4 rounded-full" style={{ backgroundColor: styles.primaryColor }}></div>
      <SkeletonElement height="24px" width="200px" />
    </div>
    <div 
      className="rounded bg-gray-200"
      style={{ 
        height
      }}
    />
  </div>
);

// Funnel Dashboard Skeleton
export const FunnelDashboardSkeleton = () => (
  <div 
    className="flex flex-col w-full p-6 glass-effect rounded-[12px] mb-6 animate-pulse"
    style={{
      backgroundColor: styles.glassEffect,
      border: `1px solid ${styles.borderColor}`,
      boxShadow: styles.cardShadow
    }}
  >
    {/* Title with accent bar */}
    <div className="flex items-center space-x-3 mb-6">
      <div className="w-2 h-8 rounded-full" style={{ backgroundColor: styles.primaryColor }}></div>
      <SkeletonElement height="24px" width="120px" />
    </div>
    
    {/* Stats display */}
    <div className="flex justify-end w-full mb-6">
      <div className="flex space-x-4 p-4 glass-effect rounded-[12px]" style={{
        backgroundColor: styles.primaryColor,
        border: `1px solid ${styles.borderColor}`,
        backdropFilter: 'blur(10px)',
        boxShadow: '0 4px 16px rgba(29, 12, 70, 0.3)'
      }}>
        {[1, 2].map((i) => (
          <div key={i} className="px-4 py-2 rounded-[8px]" style={{
            backgroundColor: styles.secondaryColor,
            color: styles.primaryColor
          }}>
            <SkeletonElement height="14px" width="60px" className="mb-2" />
            <SkeletonElement height="20px" width="40px" />
          </div>
        ))}
      </div>
    </div>

    {/* Funnel visualization skeleton */}
    <div className="w-full h-64 relative">
      <div 
        className="w-full h-full rounded bg-gray-200"
      />
    </div>
    
    {/* Titles below the funnel */}
    <div className="flex justify-between mt-4 px-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="text-center">
          <SkeletonElement height="16px" width="80px" />
        </div>
      ))}
    </div>
  </div>
);

// Traffic Sources Component Skeleton
export const TrafficSourcesSkeleton = () => (
  <div 
    className="glass-effect rounded-[12px] p-6 animate-pulse"
    style={{
      backgroundColor: styles.glassEffect,
      border: `1px solid ${styles.borderColor}`,
      boxShadow: styles.cardShadow
    }}
  >
    <div className="flex items-center mb-4">
      <div className="w-2 h-8 mr-4 rounded-full" style={{ backgroundColor: styles.primaryColor }}></div>
      <SkeletonElement height="24px" width="180px" />
    </div>
    
    {/* Chart area */}
    <div className="h-64 rounded bg-gray-200" />
    
    {/* Legend skeleton */}
    <div className="mt-4 space-y-2">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center space-x-2">
          <SkeletonElement height="12px" width="12px" className="rounded-full" />
          <SkeletonElement height="14px" width="100px" />
        </div>
      ))}
    </div>
  </div>
);

// User Type Component Skeleton
export const UserTypeSkeleton = () => (
  <div 
    className="glass-effect rounded-[12px] p-6 animate-pulse"
    style={{
      backgroundColor: styles.glassEffect,
      border: `1px solid ${styles.borderColor}`,
      boxShadow: styles.cardShadow
    }}
  >
    <div className="flex items-center mb-4">
      <div className="w-2 h-8 mr-4 rounded-full" style={{ backgroundColor: styles.primaryColor }}></div>
      <SkeletonElement height="24px" width="140px" />
    </div>
    
    {/* Chart area */}
    <div className="h-64 rounded bg-gray-200" />
    
    {/* Stats skeleton */}
    <div className="mt-4 grid grid-cols-2 gap-4">
      {[1, 2].map((i) => (
        <div key={i} className="text-center">
          <SkeletonElement height="20px" width="60px" className="mb-1" />
          <SkeletonElement height="14px" width="40px" />
        </div>
      ))}
    </div>
  </div>
);

// Retention Analytics Summary Cards Skeleton
export const RetentionSummaryCardsSkeleton = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
    {[1, 2, 3].map((i) => (
      <div 
        key={i} 
        className="glass-effect rounded-[12px] p-4 flex flex-col text-center animate-pulse"
        style={{
          backgroundColor: styles.glassEffect,
          border: `1px solid ${styles.borderColor}`,
          boxShadow: styles.cardShadow
        }}
      >
        <SkeletonElement height="16px" width="80%" className="mb-2" />
        <SkeletonElement height="24px" width="60%" className="mb-2" />
        <div className="flex items-center justify-center">
          <SkeletonElement height="14px" width="20px" className="mr-1" />
          <SkeletonElement height="14px" width="60px" />
        </div>
      </div>
    ))}
  </div>
);

// Retention Chart Skeleton
export const RetentionChartSkeleton = () => (
  <div 
    className="glass-effect rounded-[12px] p-6 mb-6 animate-pulse"
    style={{
      backgroundColor: styles.glassEffect,
      border: `1px solid ${styles.borderColor}`,
      boxShadow: styles.cardShadow
    }}
  >
    <div className="flex items-center mb-4">
      <div className="w-2 h-8 mr-4 rounded-full" style={{ backgroundColor: styles.primaryColor }}></div>
      <SkeletonElement height="24px" width="200px" />
    </div>
    <div className="h-64 md:h-80 rounded bg-gray-200" />
  </div>
);

// Retention Table Skeleton
export const RetentionTableSkeleton = () => (
  <div 
    className="glass-effect rounded-[12px] p-6 animate-pulse"
    style={{
      backgroundColor: styles.glassEffect,
      border: `1px solid ${styles.borderColor}`,
      boxShadow: styles.cardShadow
    }}
  >
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
      <div>
        <div className="flex items-center mb-2">
          <div className="w-2 h-8 mr-4 rounded-full" style={{ backgroundColor: styles.primaryColor }}></div>
          <SkeletonElement height="24px" width="180px" />
        </div>
        <SkeletonElement height="14px" width="300px" className="ml-6" />
      </div>
      
      <div className="self-start sm:self-center">
        <SkeletonElement height="36px" width="120px" className="rounded-[8px]" />
      </div>
    </div>
    
    {/* Table skeleton */}
    <div className="overflow-x-auto -mx-4 px-4 pb-4">
      <div className="min-w-max">
        <div className="animate-pulse">
          {/* Header row */}
          <div className="flex mb-2">
            <SkeletonElement height="48px" width="120px" className="mr-2" />
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <SkeletonElement key={i} height="48px" width="80px" className="mr-2" />
            ))}
          </div>
          
          {/* Data rows */}
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex mb-2">
              <SkeletonElement height="64px" width="120px" className="mr-2" />
              {[1, 2, 3, 4, 5, 6, 7, 8].map((j) => (
                <SkeletonElement key={j} height="64px" width="80px" className="mr-2" />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
    
    {/* Pagination skeleton */}
    <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
      <SkeletonElement height="16px" width="200px" />
      <div className="flex items-center gap-2">
        <SkeletonElement height="36px" width="80px" className="rounded-[8px]" />
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <SkeletonElement key={i} height="36px" width="36px" className="rounded-[8px]" />
          ))}
        </div>
        <SkeletonElement height="36px" width="80px" className="rounded-[8px]" />
      </div>
    </div>
  </div>
);

// Complete Dashboard Skeleton
export const DashboardSkeleton = () => (
  <div className="w-full px-4 md:px-6 py-6 font-poppins">
    <div className="space-y-6">
      <AnalyticsCardsSkeleton />
      <ChartSkeleton />
      <FunnelDashboardSkeleton />
      <div className="flex flex-col lg:flex-row lg:space-x-6 space-y-6 lg:space-y-0">
        <div className="w-full lg:w-1/2">
          <TrafficSourcesSkeleton />
        </div>
        <div className="w-full lg:w-1/2">
          <UserTypeSkeleton />
        </div>
      </div>
    </div>
  </div>
);

// Complete Retention Analytics Skeleton
export const RetentionAnalyticsSkeleton = () => (
  <div className="w-full px-4 md:px-6 py-6 font-poppins">
    <div className="space-y-6">
      <RetentionSummaryCardsSkeleton />
      <RetentionChartSkeleton />
      <RetentionTableSkeleton />
    </div>
  </div>
);

// Onchain Components Skeletons

// Geo Onchain Map Skeleton
export const GeoOnchainMapSkeleton = () => (
  <div 
    className="w-full flex items-center justify-center p-12 glass-effect rounded-[12px] animate-pulse"
    style={{
      backgroundColor: styles.glassEffect,
      border: `1px solid ${styles.borderColor}`,
      boxShadow: styles.cardShadow,
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      minHeight: '400px'
    }}
  >
    <div className="flex flex-col items-center">
      <div className="w-12 h-12 rounded-full bg-gray-200 mb-4" />
      <SkeletonElement height="20px" width="200px" />
    </div>
  </div>
);

// Onchain Wallet Insights Skeleton
export const OnchainWalletInsightsSkeleton = () => (
  <div className="relative min-h-screen">
    <div className="relative z-10 flex items-center justify-center min-h-[50vh]">
      <div 
        className="glass-effect rounded-[16px] p-8 text-center animate-pulse"
        style={{
          backgroundColor: styles.glassEffect,
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: `1px solid ${styles.borderColor}`,
          boxShadow: styles.cardShadow
        }}
      >
        <div className="w-12 h-12 rounded-full bg-gray-200 mx-auto mb-4" />
        <SkeletonElement height="24px" width="180px" />
      </div>
    </div>
  </div>
);

// Onchain User Insights Skeleton
export const OnchainUserInsightsSkeleton = () => (
  <div className="relative min-h-screen">
    <div className="relative z-10 flex items-center justify-center min-h-[50vh]">
      <div 
        className="glass-effect rounded-[16px] p-8 text-center animate-pulse"
        style={{
          backgroundColor: styles.glassEffect,
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: `1px solid ${styles.borderColor}`,
          boxShadow: styles.cardShadow
        }}
      >
        <div className="w-12 h-12 rounded-full bg-gray-200 mx-auto mb-4" />
        <SkeletonElement height="24px" width="200px" />
      </div>
    </div>
  </div>
);

// Onchain Market Insights Skeleton
export const OnchainMarketInsightsSkeleton = () => (
  <div className="relative min-h-screen">
    <div className="relative z-10 flex items-center justify-center min-h-[50vh]">
      <div 
        className="glass-effect rounded-[16px] p-8 text-center animate-pulse"
        style={{
          backgroundColor: styles.glassEffect,
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: `1px solid ${styles.borderColor}`,
          boxShadow: styles.cardShadow
        }}
      >
        <div className="w-12 h-12 rounded-full bg-gray-200 mx-auto mb-4" />
        <SkeletonElement height="24px" width="220px" />
      </div>
    </div>
  </div>
);

// Removed: StakingInsightsSkeleton - staking functionality removed

export const _RemovedStakingInsightsSkeleton = () => (
  <div className="relative min-h-screen">
    <div className="relative z-10 flex items-center justify-center min-h-[50vh]">
      <div 
        className="glass-effect rounded-[16px] p-8 text-center animate-pulse"
        style={{
          backgroundColor: styles.glassEffect,
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: `1px solid ${styles.borderColor}`,
          boxShadow: styles.cardShadow
        }}
      >
        <div className="w-12 h-12 rounded-full bg-gray-200 mx-auto mb-4" />
        <SkeletonElement height="24px" width="160px" />
      </div>
    </div>
  </div>
);

// Onchain Traffic Skeleton
export const OnchainTrafficSkeleton = () => (
  <div className="w-full px-4 md:px-6 py-6 font-poppins">
    <div className="space-y-6">
      {/* Header skeleton */}
      <div className="flex items-center justify-between">
        <SkeletonElement height="32px" width="200px" />
        <SkeletonElement height="40px" width="120px" className="rounded-[8px]" />
      </div>
      
      {/* Stats cards skeleton */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div 
            key={i} 
            className="glass-effect rounded-[12px] p-4 animate-pulse"
            style={{
              backgroundColor: styles.glassEffect,
              border: `1px solid ${styles.borderColor}`,
              boxShadow: styles.cardShadow
            }}
          >
            <SkeletonElement height="16px" width="60%" className="mb-2" />
            <SkeletonElement height="24px" width="80%" className="mb-2" />
            <SkeletonElement height="14px" width="40%" />
          </div>
        ))}
      </div>
      
      {/* Chart skeleton */}
      <ChartSkeleton height="400px" />
      
      {/* Table skeleton */}
      <div 
        className="glass-effect rounded-[12px] p-6 animate-pulse"
        style={{
          backgroundColor: styles.glassEffect,
          border: `1px solid ${styles.borderColor}`,
          boxShadow: styles.cardShadow
        }}
      >
        <SkeletonElement height="24px" width="150px" className="mb-4" />
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-12 bg-gray-200 rounded" />
          ))}
        </div>
      </div>
    </div>
  </div>
);

// User Journey Table Skeleton
export const UserJourneyTableSkeleton = () => (
  <div 
    className="bg-white/60 backdrop-blur-md rounded-[16px] p-8 animate-pulse"
    style={{ minHeight: '400px' }}
  >
    <div className="flex flex-col items-center space-y-4">
      <div className="w-12 h-12 rounded-full bg-gray-200" />
      <SkeletonElement height="24px" width="200px" />
    </div>
  </div>
);

// User Journey Popup Skeleton
export const UserJourneyPopupSkeleton = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-[16px] w-full max-w-4xl max-h-[90vh] overflow-hidden animate-pulse">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <SkeletonElement height="24px" width="200px" />
          <div className="w-8 h-8 rounded-full bg-gray-200" />
        </div>
      </div>
      
      {/* Content */}
      <div className="p-6">
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-200 rounded" />
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Smart Contract Filters Skeleton
export const SmartContractFiltersSkeleton = () => (
  <div className="w-full px-4 md:px-6 py-6 font-poppins">
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <SkeletonElement height="32px" width="250px" />
        <SkeletonElement height="40px" width="120px" className="rounded-[8px]" />
      </div>
      
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <SkeletonElement key={i} height="40px" width="100%" className="rounded-[8px]" />
        ))}
      </div>
      
      {/* Results */}
      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-16 bg-gray-200 rounded" />
        ))}
      </div>
    </div>
  </div>
);

// Complete Onchain Dashboard Skeleton
export const OnchainDashboardSkeleton = () => (
  <div className="w-full px-4 md:px-6 py-6 font-poppins">
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <SkeletonElement height="32px" width="200px" />
        <SkeletonElement height="40px" width="120px" className="rounded-[8px]" />
      </div>
      
      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div 
            key={i} 
            className="glass-effect rounded-[12px] p-4 animate-pulse"
            style={{
              backgroundColor: styles.glassEffect,
              border: `1px solid ${styles.borderColor}`,
              boxShadow: styles.cardShadow
            }}
          >
            <SkeletonElement height="16px" width="60%" className="mb-2" />
            <SkeletonElement height="24px" width="80%" className="mb-2" />
            <SkeletonElement height="14px" width="40%" />
          </div>
        ))}
      </div>
      
      {/* Map/Chart area */}
      <div className="h-96 bg-gray-200 rounded-[12px]" />
      
      {/* Additional sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="h-64 bg-gray-200 rounded-[12px]" />
        <div className="h-64 bg-gray-200 rounded-[12px]" />
      </div>
    </div>
  </div>
);
export default {
  AnalyticsCardSkeleton,
  AnalyticsCardsSkeleton,
  ChartSkeleton,
  FunnelDashboardSkeleton,
  TrafficSourcesSkeleton,
  UserTypeSkeleton,
  RetentionSummaryCardsSkeleton,
  RetentionChartSkeleton,
  RetentionTableSkeleton,
  DashboardSkeleton,
  RetentionAnalyticsSkeleton,
  GeoOnchainMapSkeleton,
  OnchainWalletInsightsSkeleton,
  OnchainUserInsightsSkeleton,
  OnchainMarketInsightsSkeleton,
  OnchainTrafficSkeleton,
  UserJourneyTableSkeleton,
  UserJourneyPopupSkeleton,
  SmartContractFiltersSkeleton,
  OnchainDashboardSkeleton
};

